'use client';

import React, { useEffect } from 'react';
import { Field, useField, useFormikContext } from 'formik';
import DatePicker from 'react-datepicker';
import '../../../../node_modules/react-datepicker/dist/react-datepicker.css';
import appconfig from '@/utils/appConfig';
import { ErrorDisplay } from '@/components/formComponents/';
import { setHours, setMinutes } from 'date-fns';
import { formatUkDateISO, ukNow } from '@/utils/date/ukFrame';
import { CLOSED_DATES_CONTENT_QUERYResult } from 'sanity.types';

export type ClosedDateItem =
    NonNullable<CLOSED_DATES_CONTENT_QUERYResult>[number];

interface DatePickerProps {
    name: string;
    label?: string;
    showError?: boolean;
    onChangeHandler?: (_date: Date | null) => void;
    showBorder?: boolean;
    reopenDates?: Record<string, string>;
    closedDays?: ClosedDateItem[];
    excludeSundays?: boolean;
    excludeTimes?: Date[] | null;
}

/**
 * Round a time up to the next 30-minute boundary.
 * e.g. 15:03 → 15:30, 15:30 → 16:00, 17:45 → 18:00
 */
export const roundUpToNext30 = (date: Date): Date => {
    const result = new Date(date);
    const minutes = result.getMinutes();
    if (minutes === 0) return result; // already on boundary
    const remainder = 30 - (minutes % 30);
    result.setMinutes(minutes + remainder, 0, 0);
    return result;
};

/**
 * Returns YYYY-MM-DD in LOCAL time — not UTC.
 * Critical for correct date matching against Sanity ISO date strings.
 * Export stays here (tests import from this file); body lives in ukFrame.
 */
export const toLocalISO = (date: Date): string => formatUkDateISO(date);

/**
 * Normalize closingTime by removing invisible Unicode characters (U+200B-U+200D, U+FEFF)
 * that Sanity data may contain, then trim and lowercase.
 * Prevents string comparison failures from zero-width spaces and joiners.
 */
export const normalizeClosingTime = (
    closingTime: string | null | undefined
): string => {
    return (
        closingTime
            ?.replace(/[\u200B-\u200D\uFEFF]/g, '') // Remove zero-width chars
            .trim()
            .toLowerCase() ?? ''
    );
};

/**
 * Advance a date forward until it lands on a day that is:
 * - Not a Sunday (always closed)
 * - Not a fully closed CMS day (closingTime === 'closed')
 *
 * Uses reopenDate if present, otherwise advances by 1 day.
 * Caps at 28 iterations to prevent infinite loops.
 */
export const advanceToNextOpenDay = (
    start: Date,
    closedDays?: ClosedDateItem[] | null // accept null explicitly
): Date => {
    const safeClosedDays = closedDays ?? [];
    const date = new Date(start);

    for (let i = 0; i < 28; i++) {
        // Sunday is always closed
        if (date.getDay() === 0) {
            date.setDate(date.getDate() + 1);
            continue;
        }

        const iso = toLocalISO(date);
        const fullyClosedEntry = safeClosedDays.find(
            (d) =>
                d.date === iso &&
                normalizeClosingTime(d.closingTime) === 'closed'
        );

        if (fullyClosedEntry) {
            if (fullyClosedEntry.reopenDate) {
                const [y, m, d] = fullyClosedEntry.reopenDate
                    .split('-')
                    .map(Number);
                date.setFullYear(y, m - 1, d);
                date.setHours(0, 0, 0, 0);
            } else {
                date.setDate(date.getDate() + 1);
            }
            continue;
        }

        break;
    }

    return date;
};

/**
 * Compute the default collection date based on business rules:
 * - Sunday → Monday
 * - After 18:00 → next day (Saturday after 18:00 → Monday)
 * - Fully closed CMS day → advance to first open day
 * - Time always set to 09:00
 */
export const getDefaultCollectionDate = (
    closedDays?: ClosedDateItem[] | null
): Date => {
    // UK wall clock — the depot's opening rules, not the browser's local time
    const now = ukNow();
    const candidate = new Date(now);
    candidate.setSeconds(0, 0);

    // Sunday always closed → Monday
    if (candidate.getDay() === 0) {
        candidate.setDate(candidate.getDate() + 1);
        candidate.setHours(9, 0, 0, 0);
    } else if (now.getHours() >= 18) {
        // After 6pm → next day at 09:00
        candidate.setDate(candidate.getDate() + 1);
        candidate.setHours(9, 0, 0, 0);
        // Saturday after 6pm → Sunday → push to Monday
        if (candidate.getDay() === 0) {
            candidate.setDate(candidate.getDate() + 1);
        }
    } else if (now.getHours() < 9) {
        // Before opening → open at 09:00 today
        candidate.setHours(9, 0, 0, 0);
    } else {
        // During business hours → round current time up to next 30-min slot
        const rounded = roundUpToNext30(candidate);
        // If rounding pushes past 18:00, move to next day at 09:00
        if (rounded.getHours() >= 18) {
            candidate.setDate(candidate.getDate() + 1);
            candidate.setHours(9, 0, 0, 0);
            if (candidate.getDay() === 0) {
                candidate.setDate(candidate.getDate() + 1);
            }
        } else {
            candidate.setHours(rounded.getHours(), rounded.getMinutes(), 0, 0);
        }
    }

    // Advance past any fully closed CMS days
    const open = advanceToNextOpenDay(candidate, closedDays);

    // Preserve the time that was set above
    if (
        open.getDate() !== candidate.getDate() ||
        open.getMonth() !== candidate.getMonth()
    ) {
        open.setHours(9, 0, 0, 0);
    }
    return open;
};

/**
 * Compute the default return date:
 * - Always collection date + 1 calendar day at 09:00
 * - If that day is fully closed → advance to next open day at 09:00
 * - If that day is Sunday → advance to Monday at 09:00
 */
export const getDefaultReturnDate = (
    collectionDate: Date,
    closedDays?: ClosedDateItem[] | null
): Date => {
    const candidate = new Date(collectionDate);
    candidate.setDate(candidate.getDate() + 1);
    candidate.setHours(9, 0, 0, 0);

    const open = advanceToNextOpenDay(candidate, closedDays);
    open.setHours(9, 0, 0, 0);
    return open;
};

const DatePickerField = ({
    showError = false,
    showBorder = false,
    excludeSundays = true,
    ...props
}: DatePickerProps) => {
    const [field, meta] = useField(props.name);
    const { setFieldValue, setFieldTouched, handleBlur, values } = useFormikContext();

    const [excludeTimes, setExcludeTimes] = React.useState<Date[] | null>([]);
    const [hasManualTimeChange, setHasManualTimeChange] = React.useState(false);
    const [isInitialized, setIsInitialized] = React.useState(false);

    const { name, label, onChangeHandler, closedDays } = props;

    const closedDaysRef = React.useRef<ClosedDateItem[]>(closedDays);
    closedDaysRef.current = closedDays; // synchronously updated on every render

    // Mirror the current return value synchronously so the collection-reactive
    // effect always reads the latest return without a stale closure.
    // (dep array is [collectionDate], not [field.value])
    const returnValueRef = React.useRef<Date | null | undefined>(
        field.value as Date | null
    );
    returnValueRef.current = field.value as Date | null;

    const parseAsLocalDate = (dateStr: string) => {
        // LOCAL noon, not noon UTC: react-datepicker compares excludeDates by
        // local calendar day, so the entry must sit mid-day in the browser's
        // own frame. Noon UTC is already the NEXT local day at UTC+13/+14.
        const [y, m, d] = dateStr.split('-').map(Number);
        return new Date(y, m - 1, d, 12, 0, 0);
    };

    const fullyClosedDates = closedDays
        ?.filter((closed) => {
            if (!closed.closingTime) return false;
            return normalizeClosingTime(closed.closingTime).startsWith(
                'closed'
            );
        })
        .map((closed) => parseAsLocalDate(closed.date as string));

    const isInErrorState = meta.touched && meta.error;

    const borderClass = isInErrorState
        ? 'border-2 border-red-500'
        : showBorder
          ? `border-2 ${appconfig.colors.primaryBorder}`
          : 'border-2 border-transparent';

    const isReturnDatePicker = field.name === 'returnDate';

    const isWeekday = (date: Date) => {
        const day = date.getDay();
        return day !== 0 && day !== 7;
    };

    const COLLECTION_OPENING_HOUR = 0;
    const COLLECTION_CLOSING_HOUR = 23;
    const COLLECTION_CLOSING_MINUTES = 59;
    const WEEKDAY_OPENING_HOUR = 8;
    const SATURDAY_OPENING_HOUR = 9;
    const NON_SUNDAY_CLOSING_HOUR = 18;
    const EARLY_CLOSING_HOUR = 13;

    const TIME_FORMAT = 'HH:mm';
    const DATE_FORMAT = 'EEE d MMMM, yyyy HH:mm';

    const timeSetter = (minutes: number, hours: number) =>
        setHours(setMinutes(new Date(), minutes), hours);

    const weekdayExcludeTimes = [
        timeSetter(0, 0),
        timeSetter(30, 0),
        timeSetter(0, 1),
        timeSetter(30, 1),
        timeSetter(0, 2),
        timeSetter(30, 2),
        timeSetter(0, 3),
        timeSetter(30, 3),
        timeSetter(0, 4),
        timeSetter(30, 4),
        timeSetter(0, 5),
        timeSetter(30, 5),
        timeSetter(0, 6),
        timeSetter(30, 6),
        timeSetter(0, 7),
        timeSetter(30, 7),
        timeSetter(30, 18),
        timeSetter(0, 19),
        timeSetter(30, 19),
        timeSetter(0, 20),
        timeSetter(30, 20),
        timeSetter(0, 21),
        timeSetter(30, 21),
        timeSetter(0, 22),
        timeSetter(30, 22),
        timeSetter(0, 23),
        timeSetter(30, 23),
    ];

    const saturdayExcludeTimes = [
        ...weekdayExcludeTimes,
        timeSetter(0, 8),
        timeSetter(30, 8),
    ];

    /**
     * Compute default date for this picker instance.
     * Return picker bases its default on the current Formik collection date.
     */
    const getDefaultDate = (): Date => {
        const latestClosedDays = closedDaysRef.current;

        if (isReturnDatePicker) {
            const collectionDate = (values as Record<string, unknown>)
                .collectionDate as Date | null | undefined;
            const base =
                collectionDate || getDefaultCollectionDate(latestClosedDays);
            return getDefaultReturnDate(base, latestClosedDays);
        }
        return getDefaultCollectionDate(latestClosedDays);
    };

    /**
     * Write the picked date to Formik, applying the Saturday opening-hours
     * clamp, and RETURN the value actually stored.
     *
     * The return value matters: callers must hand this same value to
     * `onChangeHandler`, never the raw `val` they passed in. `timedDate` is a
     * copy, so clamping it leaves the caller's object unchanged — passing the
     * raw object to the parent would put Formik (which the quote email reads)
     * an hour ahead of QuoteForm's `collectionDate` state (which
     * `numRentalDays` reads), silently overcharging a day. (PCH-130)
     */
    const handleDateChange = (fieldName: string, val: Date): Date => {
        const timedDate = new Date(val);
        const isSaturday = timedDate.getDay() === 6;
        const isBefore9am = timedDate.getHours() < 9;
        const isNotReturnDatePicker = fieldName !== 'returnDate';

        if (isSaturday && isBefore9am && isNotReturnDatePicker) {
            timedDate.setHours(9, 0, 0, 0);
        }

        setFieldValue(fieldName, timedDate);
        return timedDate;
    };

    const currentDate = (() => {
        const val = field.value as Date | null;
        if (!val) return getDefaultDate();
        if (isReturnDatePicker) return val;
        const iso = toLocalISO(val);
        const isOnClosed = closedDaysRef.current?.some(
            (d) =>
                d.date === iso &&
                normalizeClosingTime(d.closingTime) === 'closed'
        );
        return isOnClosed ? getDefaultDate() : val;
    })();

    const currentDayIsSaturday = currentDate.getDay() === 6;
    const localDateISO =
        currentDate instanceof Date ? toLocalISO(currentDate) : '';

    const isDayBeforeClosedDayLocal =
        closedDays?.some((d) => d.date === localDateISO) || false;
    const isEarlyClosedDayLocal =
        closedDays?.some(
            (closed) =>
                closed.date === localDateISO &&
                closed.closingTime &&
                normalizeClosingTime(closed.closingTime) !== 'closed'
        ) || false;

    const now = ukNow();
    const isCollectionToday =
        !isReturnDatePicker &&
        currentDate.getDate() === now.getDate() &&
        currentDate.getMonth() === now.getMonth() &&
        currentDate.getFullYear() === now.getFullYear();

    // Collection picker: earliest selectable DAY is the calculated default
    // collection day. When slots remain today this is today (today selectable);
    // once the depot is closed for the day the default advances to the next open
    // day, so today's calendar cell becomes non-selectable — preventing a past
    // date/time from being reached via a calendar-day click. Set to local
    // midnight because react-datepicker compares minDate at day granularity.
    // (Fourth isReturnDatePicker divergence site — see the four-guard pattern;
    // the return picker keeps ukNow() so it can still return on any day.)
    const collectionMinDate = (() => {
        const d = getDefaultCollectionDate(closedDaysRef.current);
        d.setHours(0, 0, 0, 0);
        return d;
    })();

    const shopMinTime = isReturnDatePicker
        ? new Date(0, 0, 0, COLLECTION_OPENING_HOUR)
        : isCollectionToday
          ? roundUpToNext30(now)
          : currentDayIsSaturday
            ? new Date(0, 0, 0, SATURDAY_OPENING_HOUR)
            : new Date(0, 0, 0, WEEKDAY_OPENING_HOUR);

    const shopMaxTime = isReturnDatePicker
        ? new Date(0, 0, 0, COLLECTION_CLOSING_HOUR, COLLECTION_CLOSING_MINUTES)
        : isDayBeforeClosedDayLocal || isEarlyClosedDayLocal
          ? new Date(0, 0, 0, EARLY_CLOSING_HOUR)
          : new Date(0, 0, 0, NON_SUNDAY_CLOSING_HOUR);

    useEffect(() => {
        // Initial mount — no value yet
        if (!field.value && !isInitialized) {
            const defaultDate = getDefaultDate();
            setFieldValue(field.name, defaultDate);
            setIsInitialized(true);
            if (onChangeHandler) onChangeHandler(defaultDate);
            return;
        }

        // Persist correction to Formik when current value is on a closed day.
        // Return picker is exempt — customers can return on a closed day.
        if (isInitialized && field.value && !isReturnDatePicker) {
            const iso = toLocalISO(field.value as Date);
            const isOnClosed = closedDaysRef.current?.some(
                (d: ClosedDateItem) =>
                    d.date === iso &&
                    normalizeClosingTime(d.closingTime) === 'closed'
            );
            if (isOnClosed) {
                const corrected = getDefaultDate();
                setFieldValue(field.name, corrected);
                if (onChangeHandler) onChangeHandler(corrected);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [field.value, isInitialized, closedDays?.length]);

    // For the return picker only — re-derive when collection date changes
    const collectionDate = isReturnDatePicker
        ? ((values as Record<string, unknown>).collectionDate as
              | Date
              | null
              | undefined)
        : null;

    const prevCollectionDateRef = React.useRef<Date | null | undefined>(
        collectionDate
    );

    useEffect(() => {
        if (!isReturnDatePicker) return;

        const prev = prevCollectionDateRef.current;
        prevCollectionDateRef.current = collectionDate;

        // Only react if collection date actually changed to a new value
        if (
            !collectionDate ||
            (prev && collectionDate.getTime() === prev.getTime())
        )
            return;

        // Preserve a return that still sits after the new collection.
        // Only re-default when there is no return yet, or collection has
        // caught up to / overtaken it.
        const currentReturn = returnValueRef.current;
        if (currentReturn && collectionDate.getTime() < currentReturn.getTime()) {
            return;
        }

        // Re-derive return date from the new collection date
        const newDefault = getDefaultReturnDate(
            collectionDate,
            closedDaysRef.current
        );
        setFieldValue(field.name, newDefault);
        // Un-touch so the Yup is-after-collection error (briefly set between the
        // collection-change render and this effect firing) doesn't show a red border
        // for a programmatic re-default. Re-touched on the user's next interaction.
        setFieldTouched(field.name, false, false);
        if (onChangeHandler) onChangeHandler(newDefault);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [collectionDate]);

    return (
        <div className="flex-col flex-wrap inline-flex w-full">
            {label && (
                <label className="w-full" htmlFor={name}>
                    {label}
                </label>
            )}
            <Field name={field.name} validate={true}>
                {() => (
                    <DatePicker
                        {...field}
                        {...props}
                        selected={currentDate}
                        onChangeRaw={(e) => e?.preventDefault()}
                        onChange={(date: Date | null) => {
                            if (!date) return;
                            setHasManualTimeChange(true);
                            const applied = handleDateChange(field.name, date);
                            if (onChangeHandler) onChangeHandler(applied);
                        }}
                        onSelect={(date: Date | null) => {
                            if (!date) return;
                            if (isReturnDatePicker) {
                                const currentTime =
                                    field.value || getDefaultDate();
                                date.setHours(
                                    currentTime.getHours(),
                                    currentTime.getMinutes(),
                                    0,
                                    0
                                );
                                if (date.getDay() >= 1 && date.getDay() <= 5) {
                                    setExcludeTimes(weekdayExcludeTimes);
                                } else if (date.getDay() === 6) {
                                    setExcludeTimes(saturdayExcludeTimes);
                                } else {
                                    setExcludeTimes(null);
                                }
                                const applied = handleDateChange(
                                    field.name,
                                    date
                                );
                                if (onChangeHandler) onChangeHandler(applied);
                                return;
                            }

                            if (!hasManualTimeChange) {
                                const hourChosen = date.getHours();
                                if (date.getDay() >= 1 && date.getDay() <= 5) {
                                    setExcludeTimes(weekdayExcludeTimes);
                                    if (hourChosen < 8 || hourChosen > 18)
                                        date.setHours(8, 0, 0);
                                } else if (date.getDay() === 6) {
                                    setExcludeTimes(saturdayExcludeTimes);
                                    if (hourChosen < 9 || hourChosen > 18)
                                        date.setHours(9, 0, 0);
                                } else {
                                    setExcludeTimes(null);
                                    date.setHours(9, 0, 0);
                                }
                            } else {
                                if (date.getDay() >= 1 && date.getDay() <= 5) {
                                    setExcludeTimes(weekdayExcludeTimes);
                                } else if (date.getDay() === 6) {
                                    setExcludeTimes(saturdayExcludeTimes);
                                } else {
                                    setExcludeTimes(null);
                                }
                            }
                        }}
                        excludeTimes={excludeTimes as []}
                        showTimeSelect
                        minTime={shopMinTime}
                        maxTime={shopMaxTime}
                        timeIntervals={30}
                        timeFormat={TIME_FORMAT}
                        dateFormat={DATE_FORMAT}
                        onBlur={handleBlur}
                        className={`${borderClass} p-2 relative w-full`}
                        placeholderText={'Select a date & time'}
                        autoComplete="off"
                        calendarStartDay={1}
                        minDate={isReturnDatePicker ? ukNow() : collectionMinDate}
                        excludeDates={isReturnDatePicker ? undefined : (fullyClosedDates as [])}
                        filterDate={excludeSundays ? isWeekday : undefined}
                    />
                )}
            </Field>
            {showError && <ErrorDisplay meta={meta} />}
        </div>
    );
};

export default DatePickerField;
